import { useId, type ReactElement } from 'react';
import { Link } from 'react-aria-components';
import type { MyRecognition } from '@myrewards/contracts';
import { photos } from '../assets';
import {
  ButtonLink,
  CardHelp,
  cx,
  EmptyPhoto,
  formatCount,
  formatMoney,
  formatShortDate,
  Icon,
  StatusPill,
} from '../ui';
import { AWARD_TYPE_LABEL, STAGE } from './labels';
import s from './Home.module.css';

/**
 * My recognition on a blended Home: the personal section, always last and never removed. Awards
 * from this year, or the photo empty state ("Nothing new for you this year").
 */
export function MyRecognitionPanel({
  mine,
  asOf,
  nothingYet,
  recognizeHref,
}: {
  mine: MyRecognition;
  asOf: string;
  nothingYet: boolean;
  /** Where Recognize goes for this person, or `null` when they can't start anything. */
  recognizeHref: string | null;
}): ReactElement {
  const headingId = useId();
  const year = asOf.slice(0, 4);
  const thisYear = mine.awards.filter((a) => a.awardDate.startsWith(year));
  return (
    <section className={s.panel} aria-labelledby={headingId}>
      <header className={s.panelHead}>
        <h2 id={headingId} className={cx('disp', s.panelTitle)}>
          My recognition
        </h2>
        <CardHelp
          topic="my recognition"
          shows="Your own awards as an employee: this year’s awards, their letters and any cash-or-stock choice."
          canDo="Open My rewards for every award, letter and earning statement."
        />
        <StatusPill tone="neutral" size="sm" label="Employee" />
        <Link href="/my-rewards" className={s.panelLink}>
          My rewards
          <Icon name="arrowRight" size={16} />
        </Link>
      </header>
      {thisYear.length === 0 ? (
        <div className={s.recognitionEmpty}>
          <EmptyPhoto
            src={photos.groupOfEmployeesLaughing}
            eyebrow={nothingYet ? 'Your recognition' : year}
            headline={
              nothingYet ? 'Great work gets noticed here.' : 'Nothing new for you this year.'
            }
            body={
              nothingYet
                ? 'Nothing yet. When someone recognizes you, it shows here first, with your letter and any cash-or-stock choice.'
                : 'When someone recognizes you, it shows here first. Your letters and any cash-or-stock choice will be here too.'
            }
            {...(recognizeHref !== null && {
              action: { label: 'Recognize someone', href: recognizeHref },
            })}
          />
        </div>
      ) : (
        <ul className={s.myAwards}>
          {thisYear.map((award) => {
            const stage =
              award.stage === 'employee_choice' && award.electionDue
                ? {
                    label: `Election due ${formatShortDate(award.electionDue)}`,
                    tone: 'waiting' as const,
                  }
                : STAGE[award.stage];
            return (
              <li key={award.award.id} className={s.myAward}>
                <span className={s.heldText}>
                  <Link href={`/my-rewards/${award.lineId}`} className={s.strongLink}>
                    {AWARD_TYPE_LABEL[award.type]}
                  </Link>
                  <span className={s.meta}>
                    {award.award.displayId} · {formatShortDate(award.awardDate)}
                  </span>
                </span>
                <span className={s.heldAmount}>
                  {formatMoney(award.cashCents)}
                  {award.options > 0 && ` + ${formatCount(award.options)} options`}
                </span>
                <StatusPill tone={stage.tone} size="sm" label={stage.label} />
              </li>
            );
          })}
          <li className={s.myAwardsMore}>
            <ButtonLink href="/my-rewards" variant="ghost" size="sm">
              All my awards and letters
            </ButtonLink>
          </li>
        </ul>
      )}
    </section>
  );
}
